import React from 'react';

function LandingPage({ onGetStarted, onViewApi, onViewBlog, onSignup, onLogin, currentUser, onOpenDashboard, onOpenAdmin, onOpenAstroLLM, onSignOut }) {
  const scrollToPricing = (e) => {
    e?.preventDefault();
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const isSignedIn = !!currentUser;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.tier === 'admin';
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white">
      {/* TOP NAV - Always visible on all screen sizes */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-50">
        <div className="px-4 py-3 max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🪔</span>
            <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              ASTROGEM API
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
            <button onClick={onViewApi} className="px-3 py-1.5 hover:bg-slate-800 rounded">
              📡 API Playground
            </button>
            <button onClick={scrollToPricing} className="px-3 py-1.5 hover:bg-slate-800 rounded">💰 Pricing</button>
            <button onClick={onViewBlog} className="px-3 py-1.5 hover:bg-slate-800 rounded">
              📚 Blog
            </button>
            <button onClick={onGetStarted} className="px-3 py-1.5 hover:bg-slate-800 rounded">
              🪔 Try Chart
            </button>
            {!isSignedIn ? (
              <>
                <button onClick={onLogin} className="px-3 py-1.5 hover:bg-slate-800 rounded text-white/70">
                  Sign In
                </button>
                <button onClick={onSignup} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition">
                  Sign Up Free →
                </button>
              </>
            ) : (
              <>
                <button onClick={onOpenAstroLLM} className="px-3 py-1.5 bg-gradient-to-r from-purple-600/40 to-pink-600/40 border border-purple-400/50 rounded font-semibold">
                  🪔 AstroLLM
                </button>
                {isAdmin && (
                  <button onClick={onOpenAdmin} className="px-3 py-1.5 hover:bg-red-900/40 border border-red-700/40 rounded text-red-300 text-xs font-bold">
                    🔐 Admin
                  </button>
                )}
                <button onClick={onOpenDashboard} className="px-3 py-1.5 hover:bg-slate-800 rounded">
                  📊 Dashboard
                </button>
                <span className="text-xs text-white/40 px-1">{currentUser.email || currentUser.name}</span>
                <button onClick={onSignOut} className="px-3 py-1.5 hover:bg-slate-800 rounded text-white/60 text-xs">
                  Sign out
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative px-6 py-24 max-w-7xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-block mb-4 px-4 py-1.5 bg-purple-500/20 border border-purple-400/30 rounded-full text-sm">
            ✨ NASA-grade accuracy + AI grounded in classical texts
          </div>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-amber-400 bg-clip-text text-transparent">
              ASTROGEM
            </span>
            <br />
            The Vedic API That<br />
            Quotes Real Texts
          </h1>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
            40+ endpoints powered by Swiss Ephemeris. AI grounded in 1,757 chunks
            from real classical PDFs. <strong className="text-white">60% cheaper than competitors.</strong>
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={onGetStarted}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg font-bold text-lg transition transform hover:scale-105">
              🪔 Generate Your Chart
            </button>
            <button onClick={scrollToPricing}
              className="px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/30 rounded-lg font-bold text-lg transition">
              View API Pricing
            </button>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-white/60">
            <div>✅ 110+ API endpoints</div>
            <div>✅ NASA JPL accuracy</div>
            <div>✅ 1,757 PDF citations</div>
            <div>✅ DeepSeek AI</div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="px-6 py-20 max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">Everything for Vedic Astrology</h2>
        <p className="text-center text-white/60 mb-12 max-w-2xl mx-auto">From birth charts to AI predictions — one unified API</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: '📊', title: 'Birth Charts', desc: 'Rasi, Navamsa, all 16 divisional charts (D1-D60). Sublord, dignity, vargottama analysis.' },
            { icon: '🪔', title: 'Dasha Systems', desc: 'Vimshottari, Yogini, Chara Dasha. Verified accurate to the day.' },
            { icon: '📜', title: '300+ Yogas', desc: 'Pancha Mahapurusha, Raja, Dhana, Gajakesari yogas with strength scores.' },
            { icon: '⚖️', title: 'Doshas', desc: 'Manglik, Kaal Sarpa, Sade Sati. Complete analysis with remedies.' },
            { icon: '💍', title: 'Kundli Matching', desc: '36 Guna Milan compatibility for matrimonial purposes.' },
            { icon: '🎯', title: 'Shadbala', desc: '6-fold planetary strength: Sthana, Dig, Kala, Chesta, Naisargika, Drik.' },
            { icon: '📅', title: 'Panchang', desc: 'Tithi, Karana, Yoga, Nakshatra, Hora, Rahukaal, Choghadiya.' },
            { icon: '🌟', title: 'KP System', desc: 'Krishnamurti star/sub/sub-sub lord analysis for precise predictions.' },
            { icon: '🤖', title: 'AI-Strologer', desc: 'Honest AI grounded in actual classical texts with source citations.' }
          ].map((f, i) => (
            <div key={i} className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6 hover:bg-slate-800/80 transition">
              <div className="text-4xl mb-3">{f.icon}</div>
              <h3 className="text-xl font-bold mb-2">{f.title}</h3>
              <p className="text-white/70">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CODE EXAMPLE */}
      <section className="px-6 py-16 max-w-5xl mx-auto">
        <div className="bg-slate-900/80 border border-slate-700 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-3 border-b border-slate-700 bg-slate-800/50">
            <div className="flex gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
            </div>
            <span className="text-xs text-white/50">Quick Start in 30 seconds</span>
          </div>
          <pre className="p-6 text-sm overflow-x-auto"><code className="text-green-300">{`# Install nothing. Just call the API.

curl -X POST https://api.astrogemapi.com/api/v3/vedic/birth-details \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "subject": {
      "name": "Your Name",
      "birth_data": {
        "year": 1990, "month": 5, "day": 15,
        "hour": 14, "minute": 30, "second": 0,
        "latitude": 28.6139, "longitude": 77.2090
      }
    }
  }'

# Get back: ascendant, all 9 planets, nakshatras, padas,
# sublords, dignity, combust, vargottama, navamsa — all in 100ms.`}</code></pre>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="px-6 py-20 max-w-6xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">Compare Us vs The Competition</h2>
        <div className="overflow-hidden rounded-xl border border-slate-700">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left p-4">Feature</th>
                <th className="text-center p-4 bg-purple-500/20">Our API</th>
                <th className="text-center p-4">astrology-api.io</th>
                <th className="text-center p-4">prokerala</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['NASA JPL accuracy', '✅', '✅', '✅'],
                ['Vedic dashas (3 systems)', '✅', '✅', '⚠️'],
                ['300+ Yogas', '✅', '✅', '⚠️'],
                ['AI grounded in PDFs', '🏆 ONLY US', '❌', '❌'],
                ['Source citations', '🏆 ONLY US', '❌', '❌'],
                ['Therapist mode (honest AI)', '🏆 ONLY US', '❌', '❌'],
                ['Free tier', '✅ 100/mo', '⚠️ 50/mo', '❌'],
                ['Pro tier price', '$19/mo', '$49/mo', '$30/mo'],
                ['Open source calculations', '✅', '❌', '❌']
              ].map((row, i) => (
                <tr key={i} className="border-t border-slate-700">
                  {row.map((cell, j) => (
                    <td key={j} className={`p-4 ${j === 1 ? 'bg-purple-500/10 text-center font-bold' : j === 0 ? 'font-medium' : 'text-center text-white/70'}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-6 py-20 max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">Simple, Transparent Pricing</h2>
        <p className="text-center text-white/60 mb-2">Start free. Pay only when you scale.</p>
        <p className="text-center text-white/40 text-sm mb-12">
          Hit your AI cap? Recharge anytime with credit packs (never expire). Scroll down ↓
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            {
              name: 'Free',
              price: '$0',
              period: 'forever',
              features: ['100 API calls/mo', 'All Vedic endpoints', '3 AI questions/mo', 'Community support'],
              cta: 'Start Free',
              action: 'signup',
              highlighted: false,
            },
            {
              name: 'Pro',
              price: '$19',
              period: '/month',
              features: ['10K API calls/mo', 'All endpoints + Western', '200 AI questions/mo', 'Priority support', 'Webhooks'],
              cta: 'Get Pro',
              action: 'subscribe',
              tier: 'pro',
              highlighted: false,
              subtitle: '$190/yr — save 17%',
            },
            {
              name: 'Developer',
              price: '$49',
              period: '/month',
              features: ['50K API calls/mo', 'All endpoints', '1,500 AI questions/mo', 'Recharge AI anytime', 'Email support'],
              cta: 'Get Developer',
              action: 'subscribe',
              tier: 'developer',
              highlighted: true,
              subtitle: '$490/yr — save 17%',
            },
            {
              name: 'Scale',
              price: '$199',
              period: '/month',
              features: ['500K API calls/mo', 'All endpoints', '10,000 AI questions/mo', 'Recharge AI anytime', 'Priority support'],
              cta: 'Get Scale',
              action: 'subscribe',
              tier: 'scale',
              highlighted: false,
              subtitle: '$1,990/yr — save 17%',
            },
            {
              name: 'Enterprise',
              price: 'Custom',
              period: 'pricing',
              features: ['Unlimited API calls', 'Unlimited AI', 'White-label', 'Dedicated infrastructure', 'SLA + Custom'],
              cta: 'Contact Sales',
              action: 'contact',
              highlighted: false,
            },
          ].map((plan, i) => {
            const handleClick = () => {
              if (plan.action === 'contact') {
                window.location.href = 'mailto:ashutosh@socialeyes.in?subject=AstroGem%20Enterprise%20Inquiry&body=Hi%2C%20I%27m%20interested%20in%20enterprise%20pricing%20for%20astrogemapi.com.';
              } else if (isSignedIn && onOpenDashboard) {
                // Already signed in — send to dashboard's billing tab to actually upgrade
                onOpenDashboard();
              } else if (onSignup) {
                onSignup();
              }
            };
            return (
              <div key={i} className={`rounded-2xl p-6 flex flex-col ${plan.highlighted ? 'bg-gradient-to-br from-purple-500/30 to-pink-500/20 border-2 border-purple-400 transform lg:scale-105' : 'bg-slate-800/50 border border-slate-700'}`}>
                {plan.highlighted && <div className="text-center mb-2 text-xs font-bold text-purple-300">MOST POPULAR</div>}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <div className="mb-1">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="text-white/50 ml-1 text-sm">{plan.period}</span>
                </div>
                {plan.subtitle && <p className="text-white/40 text-xs mb-4">{plan.subtitle}</p>}
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <span className="text-green-400">✓</span>
                      <span className="text-white/80">{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={handleClick}
                  className={`w-full py-2.5 rounded-lg font-bold text-sm transition ${plan.highlighted ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' : 'bg-white/10 hover:bg-white/20 border border-white/30'}`}>
                  {plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* CREDIT PACKS */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-center mb-2">🪙 AI Credit Packs</h3>
          <p className="text-center text-white/60 text-sm mb-1">Need more AI questions than your plan includes?</p>
          <p className="text-center text-white/40 text-xs mb-8">Buy credits as one-time packs. Never expire. Used after monthly allowance is exhausted.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { credits: 500, price: 9, per: '$0.018' },
              { credits: 2000, price: 29, per: '$0.0145' },
              { credits: 10000, price: 99, per: '$0.0099', best: true },
              { credits: 50000, price: 399, per: '$0.008' },
            ].map((p, i) => (
              <div key={i} className={`rounded-xl p-5 ${p.best ? 'bg-gradient-to-br from-amber-500/20 to-pink-500/10 border border-amber-500/40' : 'bg-slate-800/50 border border-slate-700'}`}>
                {p.best && <div className="text-center text-xs font-bold text-amber-300 mb-1">BEST VALUE</div>}
                <div className="text-2xl font-bold text-center">{p.credits.toLocaleString()}</div>
                <p className="text-center text-white/60 text-xs mb-3">credits</p>
                <div className="text-center text-xl font-bold mb-1">${p.price}</div>
                <p className="text-center text-white/40 text-xs mb-4">{p.per}/credit</p>
                <button onClick={() => isSignedIn && onOpenDashboard ? onOpenDashboard() : onSignup?.()}
                  className="w-full py-2 bg-slate-700 hover:bg-slate-600 rounded text-xs font-bold transition">
                  {isSignedIn ? 'Buy in Dashboard' : 'Sign up to buy'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20 text-center">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-400/30 rounded-3xl p-12">
          <h2 className="text-4xl font-bold mb-4">Ready to start?</h2>
          <p className="text-xl text-white/70 mb-8">Generate your first chart in under 30 seconds.</p>
          <button onClick={onGetStarted}
            className="px-10 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg font-bold text-lg transition transform hover:scale-105">
            🪔 Generate Your Chart Free
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 py-12 border-t border-slate-800 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          <div>
            <h4 className="font-bold mb-3">Product</h4>
            <ul className="space-y-2 text-white/60 text-sm">
              <li>API Documentation</li>
              <li>Pricing</li>
              <li>Status</li>
              <li>Changelog</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-3">Resources</h4>
            <ul className="space-y-2 text-white/60 text-sm">
              <li><button onClick={onViewBlog} className="hover:text-white">Blog</button></li>
              <li>Tutorials</li>
              <li>Vedic Glossary</li>
              <li>Examples</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-3">Company</h4>
            <ul className="space-y-2 text-white/60 text-sm">
              <li>About</li>
              <li>Contact</li>
              <li>Terms</li>
              <li>Privacy</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-3">Connect</h4>
            <ul className="space-y-2 text-white/60 text-sm">
              <li>GitHub</li>
              <li>Discord</li>
              <li>Twitter</li>
              <li>Email</li>
            </ul>
          </div>
        </div>
        <div className="text-center text-white/40 text-sm pt-8 border-t border-slate-800">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span>🪔</span>
            <span className="font-semibold">ASTROGEM API</span>
            <span>•</span>
            <span>astrogemapi.com</span>
          </div>
          © 2026 ASTROGEM API. Built with NASA-grade precision and ancient wisdom.
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
